import { FC } from "react";
import { AlertTriangleIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "danger" | "warning" | "info";
}

export const ConfirmDialog: FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  variant = "danger"
}) => {
  const { t } = useTranslation();
  const defaultConfirmText = confirmText || t("common.confirm");
  const defaultCancelText = cancelText || t("common.cancel");
  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          icon: "text-destructive",
          button: "bg-destructive text-destructive-foreground hover:bg-destructive/90"
        };
      case "warning":
        return {
          icon: "text-yellow-500",
          button: "bg-yellow-500 text-white hover:bg-yellow-600"
        };
      default:
        return {
          icon: "text-primary",
          button: "bg-primary text-primary-foreground hover:bg-primary/90"
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-background rounded-lg shadow-2xl border border-border">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangleIcon className={`h-5 w-5 ${styles.icon}`} />
            <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          </div>

          {/* Message */}
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
            {message}
          </p>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              {defaultCancelText}
            </Button>
            <Button
              onClick={handleConfirm}
              className={styles.button}
            >
              {defaultConfirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 